import { DiscountResponse } from '@models/discount';
import { OrderDiscount, OrderLine } from '@models/order';

import { makeItemKey } from './orderUtils';

interface CalculateEmployeeDiscountParams {
    orderLines: OrderLine[];
    discounts: OrderDiscount[];
    employeeDiscount: NonNullable<DiscountResponse['employeeDiscount']>;
    totalAmount: number;
}

// 상품(ITEM) 할인이 해당 주문 라인에 걸린 것인지 판단한다.
// itemKey(posId + 옵션 조합)가 있으면 그것을 우선 사용하고, 없으면 상품 ID로 대조한다.
function isDiscountOfLine(discount: OrderDiscount, line: OrderLine) {
    if (discount.itemKey) {
        return discount.itemKey === makeItemKey(line);
    }
    return !!discount.itemId && discount.itemId === line.shopItemId;
}

/**
 * 임직원 할인 금액을 계산한다.
 *
 * 할인율은 임직원 할인 대상 상품(excludedItemIds를 뺀 상품)의 '원래 금액'을 기준으로 계산하되,
 * 그 상품에 이미 적용된 다른 할인을 뺀 '잔액'을 넘지 않도록 제한한다.
 * - 상품(ITEM) 할인: 해당 상품 라인에서만 차감한다.
 * - 주문(ORDER) 할인: 상품 금액 비율대로 배분해 차감한다.
 */
function calculateEmployeeDiscount({
    orderLines, discounts, employeeDiscount, totalAmount,
}: CalculateEmployeeDiscountParams): number {
    const { discountRate, excludedItemIds } = employeeDiscount;
    // 임직원 할인 자신은 기준에서 제외한다 (파생 계산 결과가 섞여 들어오는 경우 방지)
    const otherDiscounts = discounts.filter((d) => d.type !== 'EMPLOYEE');

    const saleLines = orderLines.filter((line) => line.status === 'ON_SALE');
    const saleAmount = saleLines
        .map((line) => line.totalAmount)
        .reduce((acc, cur) => acc + cur, 0);
    const eligibleLines = saleLines
        .filter((line) => !excludedItemIds?.includes(line.shopItemId));

    const orderDiscountAmount = otherDiscounts
        .filter((d) => d.discountScope === 'ORDER')
        .map((d) => d.discountAmount)
        .reduce((acc, cur) => acc + cur, 0);

    // 할인율 계산 기준: 대상 상품의 원래 금액
    const eligibleAmount = eligibleLines
        .map((line) => line.totalAmount)
        .reduce((acc, cur) => acc + cur, 0);

    // 할인 상한: 대상 상품에 이미 적용된 다른 할인을 뺀 잔액
    const eligibleRemainingAmount = eligibleLines
        .map((line) => {
            const itemDiscountAmount = otherDiscounts
                .filter((d) => d.discountScope === 'ITEM' && isDiscountOfLine(d, line))
                .map((d) => d.discountAmount)
                .reduce((acc, cur) => acc + cur, 0);
            // 주문 할인은 이 라인이 차지하는 금액 비율만큼만 부담한다
            const sharedOrderDiscount = saleAmount > 0
                ? orderDiscountAmount * (line.totalAmount / saleAmount)
                : 0;
            return Math.max(0, line.totalAmount - itemDiscountAmount - sharedOrderDiscount);
        })
        .reduce((acc, cur) => acc + cur, 0);

    // 비율 계산 결과가 원 단위 미만으로 떨어지지 않도록 내림 처리한다
    const rawEmployeeDiscount = Math.floor(eligibleAmount * (discountRate / 100));
    // 다른 할인과 합쳐 결제 금액이 0원 밑으로 내려가지 않도록 남은 금액 내에서만 적용한다
    const otherDiscountAmount = otherDiscounts
        .map((d) => d.discountAmount)
        .reduce((acc, cur) => acc + cur, 0);
    const remainingAfterOtherDiscounts = Math.max(0, totalAmount - otherDiscountAmount);

    return Math.min(
        rawEmployeeDiscount,
        Math.floor(eligibleRemainingAmount),
        remainingAfterOtherDiscounts,
    );
}

export default calculateEmployeeDiscount;
